<template>
  <div class="chat-page">
    <div class="chat-container">
      <div class="chat-header">
        <div class="header-content">
          <div class="header-avatar">
            <img :src="assistant.avatar" alt="AI助手">
          </div>
          <div class="header-info">
            <h2>政策申报AI助手</h2>
            <p>AI大模型+海量数据库 助你一键智享红利</p>
          </div>
        </div>
      </div>
      <div class="chat-content" ref="chatContent">
        <div class="message-list">
          <div
              v-for="(msg, index) in messages"
              :key="index"
              :class="['message', msg.type]"
          >
            <div class="message-avatar">
              <img :src="msg.type === 'user' ? userInfo.avatar : assistant.avatar" :alt="msg.type">
            </div>
            <div class="message-content">
              <div class="message-info">
                <span class="message-name">{{ msg.type === 'user' ? userInfo.name : assistant.name }}</span>
                <span class="message-time">{{ msg.time }}</span>
              </div>
              <div class="message-text">
                <div v-if="msg.isThinking" class="thinking-process">
                  <div class="thinking-process-header" @click="toggleThinking(msg)">
                    <div class="thinking-process-dots">
                      <span></span>
                    </div>
                    <span class="thinking-process-title">深度思考</span>
                    <div class="thinking-process-toggle">
                      <svg v-if="!msg.thinkingCollapsed" class="toggle-icon" viewBox="0 0 24 24" width="16" height="16">
                        <path fill="currentColor" d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"></path>
                      </svg>
                      <svg v-else class="toggle-icon" viewBox="0 0 24 24" width="16" height="16">
                        <path fill="currentColor" d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"></path>
                      </svg>
                    </div>
                  </div>
                  <div class="thinking-process-content markdown-body" v-html="renderMarkdown(msg.thinkingContent)"
                       :class="{ 'collapsed': msg.thinkingCollapsed }">
                  </div>
                </div>
                <div class="markdown-body" v-html="renderMarkdown(msg.content)"></div>
              </div>
            </div>
          </div>
          <div v-if="thinking" class="message assistant">
            <div class="message-avatar">
              <img :src="assistant.avatar" alt="assistant">
            </div>
            <div class="message-content">
              <div class="message-info">
                <span class="message-name">{{ assistant.name }}</span>
              </div>
              <div class="message-text thinking">
                <div class="thinking-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span class="thinking-text">思考中</span>
              </div>
            </div>
          </div>
        </div>
        <div class="input-area">
          <div class="input-container">
            <textarea
                v-model="inputMessage"
                placeholder="请输入您的问题..."
                @keydown.enter.prevent="handleSend"
                @click="checkPhone"
                :disabled="loading"
            ></textarea>
            <div class="input-actions">
              <button
                  class="send-button"
                  @click="handleSend"
                  :disabled="loading || !inputMessage.trim()"
              >
                <span class="send-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import {defineComponent, ref, nextTick, onMounted, watch} from 'vue';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import {clickUrl, goCodeFirm} from "@/utils";
import {aiCompanyCertification, getCompanyLists, queryUserById} from "@/api/home";

// 创建 markdown-it 实例
const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  highlight: function (str, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return hljs.highlight(str, {language: lang}).value;
      } catch (__) {
      }
    }
    return ''; // 使用默认的转义
  }
});

// 自定义链接渲染
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx];
  const href = token.attrs[0][1];

  if (href.startsWith('#company-')) {
    const companyName = decodeURIComponent(href.replace('#company-', ''));
    return `<a href="javascript:void(0)" onclick="document.dispatchEvent(new CustomEvent('selectCompany', {detail: '${companyName.replace(/'/g, "\\'")}'}))">`;
  }

  return self.renderToken(tokens, idx, options);
};

export default defineComponent({
  name: 'AIChat',
  setup() {
    const messages = ref([
      {
        type: 'assistant',
        content: 'Hi~我是政策申报AI助手「政小睿」，擅长运用主流AI大模型进行政策检索/解读/匹配。',
        time: new Date().toLocaleTimeString(),
        isThinking: false,
        thinkingCollapsed: false,
      },
      {
        type: 'assistant',
        content: '现在你可以输入你企业的名称，小睿来调取该企业的最新立项记录。',
        time: new Date().toLocaleTimeString(),
        isThinking: false,
        thinkingCollapsed: false,
      }
    ]);

    // 添加折叠/展开函数
    const toggleThinking = (msg) => {
      msg.thinkingCollapsed = !msg.thinkingCollapsed;
    };

    const inputMessage = ref('');
    const thinking = ref(false);
    const loading = ref(false);
    const chatContent = ref(null);

    const userInfo = {
      name: null,
      avatar: null,
      phone: null,
    };

    const assistant = {
      name: '政小睿',
      avatar: require("../../assets/images/ai/logo.jpg")
    };

    // 滚动到底部
    const scrollToBottom = async () => {
      await nextTick();
      if (chatContent.value) {
        const messageList = chatContent.value.querySelector('.message-list');
        messageList.scrollTop = messageList.scrollHeight;
      }
    };

    // 监听消息变化，自动滚动
    watch(() => messages.value.length, scrollToBottom);
    watch(() => thinking.value, scrollToBottom);

    // 组件挂载后滚动到底部
    onMounted(async () => {
      await scrollToBottom();
      // 添加全局点击处理函数
      window.selectCompany = (companyName) => {
        inputMessage.value = companyName;
        // 可选：聚焦输入框
        setTimeout(() => {
          document.querySelector('.input-area textarea')?.focus();
        }, 100);
        handleSend()
      };
      // 检查手机号
      const urlParams = new URLSearchParams(window.location.search);
      const phone = urlParams.get('phone');
      if (phone != null && (userInfo.phone == null || userInfo.phone === "")) {
        let params = {
          phone: phone
        }
        await aiCompanyCertification(params)
      }
    });

    const handleSend = async () => {
      await checkPhone()
      const message = inputMessage.value.trim()
      if (!message || thinking.value || loading.value) return
      // 设置加载状态
      thinking.value = true
      loading.value = true

      // 添加用户消息
      messages.value.push({
        type: 'user',
        content: message,
        time: new Date().toLocaleTimeString(),
      });

      // 清空输入框
      inputMessage.value = '';

      try {
        let phone = ""
        if (userInfo.phone != null) {
          phone = userInfo.phone
        }
        let params = {
          company_name: message,
          code: 8888,
          phone: phone
        }
        let res = await aiCompanyCertification(params)
        console.log(res)
        if (res.result.certification_status === 0) {
          clickUrl('/ai/project_query')
        } else {
          let res = await getCompanyLists({keywords: message})
          let companyList = res.result || []
          let answer = ""
          if (companyList.length !== 0) {
            answer += "小睿有些拿不准，你说的是以下哪家企业？你也可以重新输入精准全称进行检索~\n\n"
            companyList.forEach(item => {
              answer += `<div class="company-item" style="color: #4776E6; cursor: pointer; margin: 8px 0;" onclick="window.selectCompany('${item.name.replace(/'/g, "\\'")}')">${item.name}</div>\n\n`
            })
          } else {
            answer += "小睿没有查到输入的企业，可以把企业名再完整一点吗？"
          }
          messages.value.push({
            type: 'assistant',
            content: answer,
            time: new Date().toLocaleTimeString(),
          });
        }
      } catch (error) {
        console.log(error)
      } finally {
        thinking.value = false;
        loading.value = false
      }
    };

    // Markdown 渲染函数
    const renderMarkdown = (content) => {
      if (!content) return '';
      try {
        return md.render(content);
      } catch (error) {
        console.error('Markdown 渲染错误:', error);
        return content;
      }
    };

    const checkPhone = async () => {
      let res = await getUser()
      if (res) {
        const urlParams = new URLSearchParams(window.location.search);
        const phone = urlParams.get('phone');
        if (phone != null && (userInfo.phone == null || userInfo.phone === "")) {
          userInfo.phone = phone
        }
        console.log("checkPhone userInfo",userInfo)
        if (userInfo.phone == null || userInfo.phone === "") {
          wx.miniProgram.navigateTo({
            url: '/pages/auth/auth' // 跳转到小程序的授权页面
          });
        }
      }
    }

    const getUser = async () => {
      if (userInfo.name != null && userInfo.avatar != null && userInfo.phone != null) return true
      try {
        let res = await queryUserById()
        userInfo.avatar = res.result.avatar
        userInfo.name = res.result.nickname
        userInfo.phone = res.result.phone
        if (res.result.certification_flag == '1') {
          inputMessage.value = res.result.company.name
        }
        return true
      } catch (res) {
        console.log(res)
        if (res.status == '2') {
          goCodeFirm({})
        }
        return false
      }
    };

    return {
      messages,
      inputMessage,
      thinking,
      userInfo,
      assistant,
      handleSend,
      chatContent,
      renderMarkdown,
      toggleThinking,
      getUser,
      loading,
      checkPhone,
    };
  }
});
</script>

<style>
/* Markdown 样式 */
.markdown-body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  line-height: 1.6;
  word-wrap: break-word;
}

.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
  margin-top: 16px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.25;
}

.markdown-body h1 {
  font-size: 2em;
}

.markdown-body h2 {
  font-size: 1.5em;
}

.markdown-body h3 {
  font-size: 1.25em;
}

.markdown-body code {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  background-color: rgba(175, 184, 193, 0.2);
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace;
}

.markdown-body pre {
  padding: 16px;
  overflow: auto;
  font-size: 85%;
  line-height: 1.45;
  background-color: #f6f8fa;
  border-radius: 6px;
  margin-top: 8px;
  margin-bottom: 8px;
}

.markdown-body pre code {
  padding: 0;
  margin: 0;
  font-size: 100%;
  word-break: normal;
  white-space: pre;
  background: transparent;
  border: 0;
}

.markdown-body blockquote {
  padding: 0 1em;
  color: #57606a;
  border-left: 0.25em solid #d0d7de;
  margin: 8px 0;
}

.markdown-body ul,
.markdown-body ol {
  padding-left: 2em;
  margin-top: 8px;
  margin-bottom: 8px;
}

.markdown-body table {
  border-spacing: 0;
  border-collapse: collapse;
  margin: 8px 0;
  width: 100%;
}

.markdown-body table th,
.markdown-body table td {
  padding: 6px 13px;
  border: 1px solid #d0d7de;
}

.markdown-body table tr {
  background-color: #ffffff;
  border-top: 1px solid #c6cbd1;
}

.markdown-body table tr:nth-child(2n) {
  background-color: #f6f8fa;
}

.markdown-body img {
  max-width: 100%;
  box-sizing: content-box;
  background-color: #fff;
  border-radius: 3px;
}

.markdown-body p {
  margin-top: 0;
  margin-bottom: 10px;
}

/* 暗黑模式下的 Markdown 样式 */
@media (prefers-color-scheme: dark) {
  .markdown-body code {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .markdown-body pre {
    background-color: #1e1e1e;
  }

  .markdown-body blockquote {
    color: #8b949e;
    border-left-color: #30363d;
  }

  .markdown-body table th,
  .markdown-body table td {
    border-color: #30363d;
  }

  .markdown-body table tr {
    background-color: transparent;
    border-color: #30363d;
  }

  .markdown-body table tr:nth-child(2n) {
    background-color: #161b22;
  }

  .markdown-body img {
    background-color: #1e1e1e;
  }
}
</style>

<style scoped>
/* 思考过程样式 - DeepSeek风格 */
.thinking-process {
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 6px;
  margin-bottom: 8px;
}

.thinking-process-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  user-select: none;
}

.thinking-process-header:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.thinking-process-toggle {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

.thinking-process-toggle:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.toggle-icon {
  transition: transform 0.2s ease;
}

.thinking-process-dots {
  display: flex;
  gap: 4px;
  margin-right: 8px;
}

.thinking-process-dots span {
  width: 6px;
  height: 6px;
  background-color: #1989fa;
  border-radius: 50%;
  display: inline-block;
  animation: thinking-process 1.4s infinite;
}

.thinking-process-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.thinking-process-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes thinking-process {
  0%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  50% {
    transform: translateY(-2px);
    opacity: 1;
  }
}

.thinking-process-title {
  font-size: 12px;
  color: #888;
  font-weight: 500;
}

.thinking-process-content {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  white-space: pre-wrap;
  transition: all 0.3s ease-in-out;
  overflow: hidden;
  padding: 0 12px 12px 12px;
}

.thinking-process-content.collapsed {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
}

.thinking-process-header .toggle-icon {
  margin-left: auto;
  transition: transform 0.3s ease;
}

.thinking-process-header .toggle-icon.collapsed {
  transform: rotate(-180deg);
}


/* 暗黑模式 */
@media (prefers-color-scheme: dark) {
  .thinking-process {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .thinking-process-content {
    color: #aaa;
  }

  .thinking-process-dots span {
    background-color: #1989fa;
  }

  .thinking-process-title {
    color: #bbb;
  }

  .thinking-process-header:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .thinking-process-toggle:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
}


/* 基础样式 */
.chat-page {
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  display: flex;
  flex-direction: column;
}

.chat-container {
  flex: 1;
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1), 0 1px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: none;
}

/* 头部样式 */
.chat-header {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: linear-gradient(to right, #4776E6, #1989fa);
  color: white;
  border-radius: 16px 16px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-avatar {
  width: 48px;
  height: 48px;
  border-radius: 24px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.header-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.header-info h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.header-info p {
  margin: 4px 0 0;
  font-size: 14px;
  opacity: 0.8;
}

/* 内容区域 */
.chat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #f9f9f9;
  position: relative;
  height: calc(100vh - 180px); /* 减去头部和其他固定元素的高度 */
}

.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  padding-bottom: 100px; /* 为底部输入框留出空间 */
  scroll-behavior: smooth;
  height: calc(100% - 80px); /* 减去输入区域的高度 */
}

/* 消息样式 */
.message {
  display: flex;
  margin-bottom: 24px;
  gap: 12px;
  opacity: 0;
  animation: fadeIn 0.3s forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message.user {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid white;
}

.message-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.message-content {
  max-width: 70%;
}

.message.user .message-content {
  align-items: flex-end;
}

.message-info {
  margin-bottom: 4px;
  display: flex;
  gap: 8px;
  align-items: center;
}

.message-name {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.message-time {
  color: #999;
  font-size: 12px;
}

.message-text {
  padding: 12px 16px;
  background-color: white;
  border-radius: 18px;
  font-size: 15px;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.2s;
}

.message.assistant .message-text {
  border-top-left-radius: 4px;
  background-color: white;
}

.message.user .message-text {
  border-top-right-radius: 4px;
  background: linear-gradient(135deg, #6e8efb, #1989fa);
  color: white;
  box-shadow: 0 2px 8px rgba(110, 142, 251, 0.3);
}

/* 思考中动画样式 */
.thinking {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  min-width: 100px;
}

.thinking-dots {
  display: flex;
  gap: 4px;
}

.thinking-dots span {
  width: 8px;
  height: 8px;
  background-color: #1989fa;
  border-radius: 50%;
  display: inline-block;
  animation: thinking 1.4s infinite;
}

.thinking-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.thinking-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes thinking {
  0%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  50% {
    transform: translateY(-8px);
    opacity: 1;
  }
}

.thinking-text {
  color: #666;
  font-size: 14px;
}

/* 输入区域 */
.input-area {
  padding: 16px;
  background-color: white;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.input-container {
  position: relative;
  border-radius: 24px;
  background-color: #f5f5f5;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.input-container:focus-within {
  box-shadow: 0 2px 12px rgba(110, 142, 251, 0.2);
  background-color: white;
}

.input-area textarea {
  width: 100%;
  min-height: 24px;
  max-height: 120px;
  padding: 16px 60px 16px 20px;
  border: none;
  border-radius: 24px;
  resize: none;
  font-size: 15px;
  line-height: 1.5;
  box-sizing: border-box;
  background: transparent;
  outline: none;
  color: #333;
}

.input-area textarea::placeholder {
  color: #999;
}

.input-actions {
  position: absolute;
  right: 8px;
  bottom: 8px;
}

.send-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6e8efb, #1989fa);
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(110, 142, 251, 0.3);
  transition: all 0.2s;
}

.send-button:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(110, 142, 251, 0.4);
}

.send-button:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.send-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .chat-page {
    padding: 0;
  }

  .chat-container {
    height: 100vh;
    border-radius: 0;
    max-width: 100%;
  }

  .chat-header {
    border-radius: 0;
    padding: 12px;
  }

  .header-avatar {
    width: 40px;
    height: 40px;
  }

  .message-list {
    padding: 16px;
  }

  .message-text {
    font-size: 14px;
    padding: 10px 14px;
  }

  .input-area {
    padding: 12px;
  }

  .input-area textarea {
    padding: 12px 50px 12px 16px;
    font-size: 14px;
  }

  .send-button {
    width: 36px;
    height: 36px;
  }
}

/* 小屏幕手机 */
@media (max-width: 480px) {
  .message-content {
    max-width: 80%;
  }

  .message-avatar {
    width: 32px;
    height: 32px;
  }
}

/* 暗黑模式支持 */
@media (prefers-color-scheme: dark) {
  .chat-page {
    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  }

  .chat-container {
    background-color: rgba(30, 41, 59, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .chat-content {
    background-color: #111827;
  }

  .message-text {
    background-color: #1e293b;
    color: #e5e7eb;
  }

  .message.assistant .message-text {
    background-color: #1e293b;
  }

  .message-name {
    color: #e5e7eb;
  }

  .input-container {
    background-color: #1e293b;
  }

  .input-container:focus-within {
    background-color: #2d3748;
  }

  .input-area textarea {
    color: #e5e7eb;
  }

  .input-area textarea::placeholder {
    color: #9ca3af;
  }

  .thinking-text {
    color: #9ca3af;
  }
}
</style>